import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LocationData } from '@/components/common/CemeteryLocationInput';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface EditableMemorial {
  id: string;
  first_name: string;
  last_name: string | null;
  client_email: string | null;
  contact_phone: string | null;
  intended_cemetery_name?: string | null;
  intended_city?: string | null;
  intended_latitude?: number | null;
  intended_longitude?: number | null;
  headstone_type_id?: string | null;
  stone_count?: string | null;
  is_double_memorial?: boolean | null;
  person2_first_name?: string | null;
  person2_last_name?: string | null;
}

interface EditMemorialDialogProps {
  memorial: EditableMemorial | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  headstoneTypes: Array<{ id: string; name: string; applicable_stone_count: string }> | undefined;
}

export function EditMemorialDialog({
  memorial,
  isOpen,
  onOpenChange,
  onSuccess,
  headstoneTypes,
}: EditMemorialDialogProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [location, setLocation] = useState<LocationData | null>(null);
  const [headstoneTypeId, setHeadstoneTypeId] = useState('');
  const [stoneCount, setStoneCount] = useState<'single' | 'double' | ''>('');
  const [isDoubleMemorial, setIsDoubleMemorial] = useState(false);
  const [person2FirstName, setPerson2FirstName] = useState('');
  const [person2LastName, setPerson2LastName] = useState('');
  const [person2HasDifferentLastName, setPerson2HasDifferentLastName] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-populate form when memorial changes
  useEffect(() => {
    if (memorial) {
      setFirstName(memorial.first_name || '');
      setLastName(memorial.last_name || '');
      setClientEmail(memorial.client_email || '');
      setContactPhone(memorial.contact_phone || '');
      setLocation({
        cemeteryName: memorial.intended_cemetery_name || null,
        city: memorial.intended_city || null,
        latitude: memorial.intended_latitude || null,
        longitude: memorial.intended_longitude || null,
      });
      setHeadstoneTypeId(memorial.headstone_type_id || '');
      setStoneCount((memorial.stone_count as 'single' | 'double' | '') || '');
      setIsDoubleMemorial(memorial.is_double_memorial || false);
      setPerson2FirstName(memorial.person2_first_name || '');
      const p2Last = memorial.person2_last_name || '';
      setPerson2LastName(p2Last);
      setPerson2HasDifferentLastName(!!(p2Last.trim() && p2Last.trim() !== (memorial.last_name || '').trim()));
      setError(null);
    }
  }, [memorial]);

  const handleSubmit = async () => {
    if (!memorial) return;

    if (!firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!clientEmail.trim() && !contactPhone.trim()) {
      setError('Please provide an email or phone number');
      return;
    }
    if (clientEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(clientEmail.trim())) {
        setError('Please enter a valid email address');
        return;
      }
    }
    if (!stoneCount) {
      setError('Please select Single or Double');
      return;
    }
    if (!headstoneTypeId) {
      setError('Please select a headstone type');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: updatedRows, error: updateError } = await supabase
        .from('memorials')
        .update({
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          client_email: clientEmail.trim() || null,
          contact_phone: contactPhone.trim() || null,
          intended_cemetery_name: location?.cemeteryName || null,
          intended_city: location?.city || null,
          intended_latitude: location?.latitude || null,
          intended_longitude: location?.longitude || null,
          headstone_type_id: headstoneTypeId || null,
          stone_count: stoneCount || null,
          is_double_memorial: isDoubleMemorial,
          person2_first_name: isDoubleMemorial ? person2FirstName.trim() || null : null,
          person2_last_name: isDoubleMemorial && person2HasDifferentLastName
            ? person2LastName.trim() || null
            : null,
        })
        .eq('id', memorial.id)
        .select('id');

      if (updateError) throw updateError;
      // RLS-blocked updates return no error and an empty result set. Without this
      // check the toast would falsely report success while nothing persisted.
      if (!updatedRows || updatedRows.length === 0) {
        throw new Error("You don't have permission to edit this memorial.");
      }

      toast.success('Memorial updated');
      onSuccess();
    } catch (err: unknown) {
      console.error('Error updating memorial:', err);
      setError(err instanceof Error ? err.message : 'Failed to update memorial');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Memorial</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>First Name <span className="text-destructive">*</span></Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
          </div>
          <div className="space-y-2">
            <Label>Last Name</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="double-memorial-toggle">Double Memorial</Label>
            <Switch
              id="double-memorial-toggle"
              checked={isDoubleMemorial}
              onCheckedChange={(checked) => {
                setIsDoubleMemorial(checked);
                if (!checked) {
                  setPerson2FirstName('');
                  setPerson2LastName('');
                  setPerson2HasDifferentLastName(false);
                }
              }}
            />
          </div>
          {isDoubleMemorial && (
            <>
              <div className="space-y-2">
                <Label>Second Person's First Name</Label>
                <Input value={person2FirstName} onChange={(e) => setPerson2FirstName(e.target.value)} placeholder="First name of second person" />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="different-last-name-toggle" className="text-sm font-normal text-muted-foreground cursor-pointer">
                  They had different last names
                </Label>
                <Switch
                  id="different-last-name-toggle"
                  checked={person2HasDifferentLastName}
                  onCheckedChange={(checked) => {
                    setPerson2HasDifferentLastName(checked);
                    if (!checked) setPerson2LastName('');
                  }}
                />
              </div>
              {person2HasDifferentLastName && (
                <div className="space-y-2">
                  <Label>Second Person's Last Name</Label>
                  <Input value={person2LastName} onChange={(e) => setPerson2LastName(e.target.value)} placeholder="Last name of second person" />
                </div>
              )}
            </>
          )}
          <div className="space-y-2">
            <Label>Client Email</Label>
            <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="client@example.com" />
          </div>
          <div className="space-y-2">
            <Label>Client Phone</Label>
            <Input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="(555) 123-4567" />
            {!clientEmail.trim() && !contactPhone.trim() && (
              <p className="text-xs text-amber-600">At least one of email or phone is required</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Stone Configuration <span className="text-destructive">*</span></Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={stoneCount === 'single' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => {
                  if (stoneCount !== 'single') {
                    setStoneCount('single');
                    const stillValid = headstoneTypes?.some(
                      (ht) => ht.id === headstoneTypeId && (ht.applicable_stone_count === 'both' || ht.applicable_stone_count === 'single')
                    );
                    if (!stillValid) setHeadstoneTypeId('');
                  }
                }}
              >
                Single
              </Button>
              <Button
                type="button"
                variant={stoneCount === 'double' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => {
                  if (stoneCount !== 'double') {
                    setStoneCount('double');
                    const stillValid = headstoneTypes?.some(
                      (ht) => ht.id === headstoneTypeId && (ht.applicable_stone_count === 'both' || ht.applicable_stone_count === 'double')
                    );
                    if (!stillValid) setHeadstoneTypeId('');
                  }
                }}
              >
                Double
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Headstone Type <span className="text-destructive">*</span></Label>
            <Select
              value={headstoneTypeId}
              onValueChange={setHeadstoneTypeId}
              disabled={!stoneCount}
            >
              <SelectTrigger>
                <SelectValue placeholder={stoneCount ? 'Select headstone type...' : 'Select Single or Double first'} />
              </SelectTrigger>
              <SelectContent>
                {headstoneTypes
                  ?.filter((ht) => !stoneCount || ht.applicable_stone_count === 'both' || ht.applicable_stone_count === stoneCount)
                  .map((ht) => (
                    <SelectItem key={ht.id} value={ht.id}>{ht.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
